import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTheSeaContext } from "./TheSeaContext";
import { useFocusAgent } from "./TheSeaLayout";
import SEO from "../SEO";
import ConversationHistory from "./ConversationHistory";
import config from "../../config";
import api, { baseURL } from "../../utils/api";

export default function TheSeaAccount() {
  const { accountEmail, pointsBalance, isLoggedIn } = useTheSeaContext();
  const focusAgent = useFocusAgent();
  const [feedback, setFeedback] = useState("");
  const [replyEmail, setReplyEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackMessageColor, setFeedbackMessageColor] = useState("#c0392b");

  const showPlural = pointsBalance !== 1;

  const sendFeedback = async () => {
    if (!feedback.trim()) {
      return;
    }

    setSending(true);
    setFeedbackMessage("");

    try {
      // Signed-in senders don't need to supply an email — the backend resolves a reply-to
      // address itself from the account's JWT, so replyEmail is only relevant (and only
      // shown in the UI) for anonymous visitors.
      await api.post(`${baseURL}/anonymous/send_contact_email`, {
        title: "Feedback from askTheSea",
        message: feedback.trim(),
        email: replyEmail.trim(),
      });

      setFeedbackMessage("Thanks — your feedback has been sent!");
      setFeedbackMessageColor("#2e7d32");
      setFeedback("");
      setReplyEmail("");
    } catch (err) {
      console.error(err);
      setFeedbackMessage("Failed to send feedback. Please try again later.");
      setFeedbackMessageColor("#c0392b");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <SEO
      title="My Account — askTheSea"
      description="Manage your askTheSea account and point balance."
      url={`${config.BaseUrl}/account`}
      image={`${config.BaseUrl}/assets/logo.png?v=2`}
      noindex
    />
    <section className="page active" id="page-account">
      <div className="wrap about-hero">
        <div className="eyebrow">Account</div>
        <h1>My Account</h1>
        <p className="lede" id="accountEmailLine">
          {accountEmail
            ? `Signed in as ${accountEmail}`
            : "You haven't created an account yet — try askTheSea with a few questions and you'll be prompted to create one for free."}
        </p>
      </div>

      <div className="wrap">
        <div className="legal-content account-content">
          <section className="tight">
            <h2>Point balance</h2>
            <p>
              You have <strong id="accountPointsValue">{pointsBalance}</strong>
              {" "}point{showPlural && <span id="accountPointsPlural">s</span>} remaining. Points are used one for each question you send to{" "}
              <a href="#ask" onClick={(e) => { e.preventDefault(); focusAgent(); }}>askTheSea</a>.
            </p>
            <Link to="/points" className="btn btn-primary btn-sm">add points</Link>
          </section>

          {isLoggedIn && <ConversationHistory />}

          <section className="tight feedback-outdent">
            <h2 style={{ fontSize: "clamp(2rem, 3.6vw, 2.7rem)" }}>Feedback</h2>
            <p>Have a suggestion or run into a problem? Send us a note and we'll get back to you within 2 business days.</p>
            <textarea
              id="accountFeedbackText"
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Tell us what's on your mind..."
              rows={4}
              disabled={sending}
              style={{ width: "100%", maxWidth: "480px", padding: "10px 12px", borderRadius: "6px", border: "1px solid var(--border)", fontSize: "16px", fontFamily: "inherit", resize: "vertical" }}
            />
            {!isLoggedIn && (
              <div style={{ marginTop: "10px" }}>
                <label htmlFor="accountFeedbackEmail" style={{ display: "block", fontSize: "0.85rem", marginBottom: "4px", color: "var(--muted, #666)" }}>
                  Your email (optional)
                </label>
                <input
                  id="accountFeedbackEmail"
                  type="email"
                  value={replyEmail}
                  onChange={(e) => setReplyEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={sending}
                  style={{ width: "100%", maxWidth: "480px", padding: "10px 12px", borderRadius: "6px", border: "1px solid var(--border)", fontSize: "16px", fontFamily: "inherit" }}
                />
              </div>
            )}
            <div style={{ marginTop: "12px" }}>
              <button className="btn btn-primary" onClick={sendFeedback} disabled={sending || !feedback.trim()}>
                {sending ? "Sending..." : "Send Feedback"}
              </button>
            </div>
            {feedbackMessage && (
              <p style={{ marginTop: "10px", color: feedbackMessageColor, fontSize: "0.9rem" }}>{feedbackMessage}</p>
            )}
          </section>
        </div>
      </div>
    </section>
    </>
  );
}
